/**
 * ChatNoticeUtil - I word the clauses a chat notice is assembled from. ONLY concerned with turning
 * counts into English - no state, no notice setting.
 */
export const ChatNoticeUtil = {
  /**
   * The sentence around it differs by why the prompts were dropped - a navigation and a failed
   * send both say it - so only the clause they share lives here.
   */
  droppedPrompts({ dropped }: { dropped: number }) {
    return `${dropped} queued ${
      dropped === 1 ? 'prompt was' : 'prompts were'
    } not sent`;
  },

  /**
   * One sentence for a 423 wherever it lands, and not the server's: its own words name the entity
   * and its id, neither of which is for the reader.
   *
   * **The action leads where there is one.** "This chat is blocked, cannot rename chat" says the
   * same thing twice; "Renaming this chat is blocked" says it once. The send has no action to name -
   * the chat itself is what is blocked - so it keeps the bare sentence.
   */
  locked({ action }: { action?: string } = {}) {
    const subject = action ? `${action} this chat` : 'This chat';

    return `${subject} is currently blocked by another process.`;
  },

  /**
   * **The one notice that is not about this send.** The conversation itself is finished, so it says
   * so plainly and names the way on rather than inviting a retry that cannot work.
   *
   * `error` rather than the default warning, for the reason `stopper` carries it: a caution is
   * about a moment that will pass, and this is a wall.
   *
   * **The copy comes first because it is the only part with a deadline.** Starting a new chat is
   * still there in a minute; the answers in this one are what a reader would not think to save
   * until they had already moved on.
   *
   * The middle clause is there to say whose limit it is: a red banner on a send that worked fine
   * all morning reads as something the reader broke.
   */
  contextFull() {
    return {
      message:
        'This chat is full and cannot take any more messages. That is a ChatGPT limit, not anything you did wrong. Copy any answers you want to keep, then start a new chat.',
      type: 'error',
    } as const;
  },

  /**
   * For the failures nothing can be done about, so it neither apologises for a moment nor invites a
   * retry that will fail the same way. Carries `error` rather than the default warning: this is not
   * a caution about what just happened, it is a wall.
   */
  stopper() {
    return {
      message:
        'Something has gone wrong at our end and this chat is unavailable. Please come back and try again later.',
      type: 'error',
    } as const;
  },

  /**
   * The send's own lock, which says one thing more than the others: the words are still in the
   * composer. Nothing was lost, and the answer is to press send again in a moment - so saying so
   * saves the reader wondering whether to retype.
   */
  lockedSend({ attached }: { attached: number }) {
    const locked = ChatNoticeUtil.locked();

    if (attached === 0) {
      return `${locked} Your prompt is still in the composer.`;
    }

    return attached === 1
      ? `${locked} Your prompt and its attachment are still in the composer.`
      : `${locked} Your prompt and its ${attached} attachments are still in the composer.`;
  },

  /**
   * Hiding is the one that can simply be repeated, so it says so. A rename cannot: its field has
   * already closed by the time this is read, so there is nothing left to press.
   */
  lockedHide() {
    return `${ChatNoticeUtil.locked({ action: 'Hiding' })} Try again once it's done.`;
  },

  lockedUnhide() {
    return `${ChatNoticeUtil.locked({ action: 'Unhiding' })} Try again once it's done.`;
  },

  /** The field stays open on this one, so the name is still there to press send on again. */
  lockedRename() {
    return `${ChatNoticeUtil.locked({ action: 'Renaming' })} Your new name wasn't saved.`;
  },

  /**
   * A file the server would not take. The first failure could be the network, so it asks for another
   * press; the second says the file is the problem and offers the way past it. Named either way -
   * with several attached, nothing else says which one.
   */
  uploadFailed({ failures, fileName }: { failures: number; fileName: string }) {
    return failures < 2
      ? `We couldn't upload ${fileName}. Everything is still in the composer - try again.`
      : `We still couldn't upload ${fileName}. Remove it, or re-attach it, and send again.`;
  },

  /** Names the files only when there were some, and counts them once there is more than one. */
  sendFailed({ attached }: { attached: number }) {
    if (attached === 0)
      return "We couldn't send that. Your prompt is back in the composer.";

    return attached === 1
      ? "We couldn't send that. Your prompt and its attachment are back in the composer."
      : `We couldn't send that. Your prompt and its ${attached} attachments are back in the composer.`;
  },
};
